// Slice 5: Proof Engine.
import { useEffect, useState } from "react";
import { FolderCheck, Plus, ExternalLink, TrendingUp, Sparkles } from "lucide-react";
import VirenSuggestionsPanel, { type ProofSuggestion } from "../../components/viren-suggestions-panel";

const BASE = import.meta.env.BASE_URL;
const auth = (): Record<string, string> => ({ "x-founder-token": localStorage.getItem("founder_token") || "" });

interface Proof {
  id: number; kind: string; title: string; url: string | null;
  describesWhat: string | null; readinessComponentKey: string | null;
  readinessImpact: number; addedByName: string; addedByKind: string;
  addedViaSiaPrompt: boolean; createdAt: string;
}
interface Component { key: string; label: string; }

function fmt(iso: string) { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }

export default function FounderProof() {
  const [proofs, setProofs] = useState<Proof[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", describesWhat: "", componentKey: "" });
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await fetch(`${BASE}api/founder-portal/proof`, { headers: auth() });
      if (!r.ok) { setErr("Could not load proofs."); return; }
      const d = await r.json();
      setProofs(d.proofs); setComponents(d.components);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.url.trim() || busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${BASE}api/founder-portal/proof`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({
          title: form.title.trim(), url: form.url.trim(),
          describesWhat: form.describesWhat.trim() || undefined,
          readinessComponentKey: form.componentKey || undefined,
        }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); setErr(j?.error || "Could not add"); return; }
      setForm({ title: "", url: "", describesWhat: "", componentKey: "" });
      setShowForm(false);
      await load();
    } finally { setBusy(false); }
  }

  function applySuggestion(s: ProofSuggestion) {
    setForm({
      title: s.title,
      url: "",
      describesWhat: s.whatItProves,
      componentKey: s.readinessComponentKey || "",
    });
    setShowForm(true);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="input-proof-url"]') as HTMLInputElement | null;
      el?.focus();
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  }

  if (loading) return <div className="p-12 text-white/60 text-sm">Loading.</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-2">Proof Engine</div>
        <h1 className="text-2xl font-bold tracking-tight mb-2">Files and links that move your readiness</h1>
        <p className="text-sm text-white/65 leading-relaxed max-w-2xl">
          Add a Drive link, a public file, or a customer letter. Each proof is timestamped, attributed, and shows how much it changed your readiness score.
        </p>
      </div>

      <VirenSuggestionsPanel kind="proof" onUseProof={applySuggestion} />

      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          data-testid="button-add-proof"
          className="mb-5 inline-flex items-center gap-2 bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white text-sm font-semibold rounded-md px-4 py-2"
        ><Plus className="w-3.5 h-3.5" /> Add a proof link</button>
      ) : (
        <form onSubmit={submit} className="mb-5 bg-white/[0.04] border border-white/[0.08] rounded-xl p-4 space-y-3">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-white/60 block mb-1">What is this</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={200}
              placeholder="Government PO from XYZ University"
              data-testid="input-proof-title"
              className="w-full bg-black/40 border border-white/[0.12] rounded-md px-3 py-2 text-sm text-white outline-none focus:border-[#FF5C00]/50"
            />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-white/60 block mb-1">Public link or Drive link</label>
            <input
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://drive.google.com/..."
              data-testid="input-proof-url"
              className="w-full bg-black/40 border border-white/[0.12] rounded-md px-3 py-2 text-sm text-white outline-none focus:border-[#FF5C00]/50"
            />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-white/60 block mb-1">What does this prove</label>
            <input
              value={form.describesWhat}
              onChange={(e) => setForm({ ...form, describesWhat: e.target.value })}
              maxLength={200}
              placeholder="A signed PO with a real customer"
              className="w-full bg-black/40 border border-white/[0.12] rounded-md px-3 py-2 text-sm text-white outline-none focus:border-[#FF5C00]/50"
            />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-white/60 block mb-1">Which part of readiness does this strengthen</label>
            <select
              value={form.componentKey}
              onChange={(e) => setForm({ ...form, componentKey: e.target.value })}
              className="w-full bg-black/40 border border-white/[0.12] rounded-md px-3 py-2 text-sm text-white outline-none focus:border-[#FF5C00]/50"
            >
              <option value="">Pick one (optional)</option>
              {components.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          {err && <p className="text-xs text-red-400">{err}</p>}
          <div className="flex gap-2 pt-1">
            <button
              type="submit" disabled={busy || !form.title.trim() || !form.url.trim()}
              data-testid="button-submit-proof"
              className="bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-40 text-white text-sm font-semibold rounded-md px-4 py-2"
            >{busy ? "Adding" : "Add proof"}</button>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm text-white/60 hover:text-white px-3 py-2">Cancel</button>
          </div>
        </form>
      )}

      {proofs.length === 0 ? (
        <div className="bg-black/30 border border-white/[0.08] rounded-xl p-10 text-center">
          <FolderCheck className="w-8 h-8 text-white/60 mx-auto mb-3" />
          <p className="text-sm text-white/60">No proofs yet. Add your first one above.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {proofs.map(p => (
            <div key={p.id} className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-4">
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  {p.addedViaSiaPrompt && <Sparkles className="w-3.5 h-3.5 text-[#FF5C00] flex-shrink-0" />}
                  <h3 className="text-sm font-semibold truncate">{p.title}</h3>
                  {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-[#FF5C00]/80 hover:text-[#FF5C00]"><ExternalLink className="w-3 h-3" /></a>}
                </div>
                {p.readinessImpact > 0 && (
                  <span className="text-[10px] font-mono bg-green-500/10 border border-green-500/30 text-green-300 rounded px-2 py-0.5 flex items-center gap-1 flex-shrink-0">
                    <TrendingUp className="w-3 h-3" /> +{p.readinessImpact}
                  </span>
                )}
              </div>
              {p.describesWhat && <p className="text-xs text-white/65 mb-1.5">{p.describesWhat}</p>}
              <div className="flex items-center gap-2 text-[10px] text-white/60">
                <span>Added by {p.addedByName}</span>
                <span>·</span>
                <span>{fmt(p.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
